import React, { useEffect, useState } from "react";
import { Box, Paper, Tabs, Tab, Alert, Snackbar, useMediaQuery, useTheme as useMuiTheme } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { LocationOn, Map, ViewList } from '@mui/icons-material';
import API_BASE_URL from "../apiConfig";
import { Link } from "react-router-dom";
import { useTheme } from "../ThemeContext";
import MKTypography from "../components/MKTypography";
import MKButton from "../components/MKButton";
import { getDataGridStyles } from "../styles/dataGridStyles";
import ClubMapView from "../components/ClubMapView";



const ClubsPage = () => {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.only("xs"), { noSsr: true });
  const isMobileOrSmall = useMediaQuery(muiTheme.breakpoints.down('sm'), { noSsr: true });
  

  const [clubs, setClubs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [showLocationAlert, setShowLocationAlert] = useState(false);

  // Define all possible DataGrid columns
  const allColumns = [
    { 
      field: 'name', 
      headerName: 'Name', 
      flex: 1, 
      minWidth: (isMobile || isMobileOrSmall) ? 120 : 150,
      renderCell: (params) => (
        <Link 
          to={`/clubs/${params.row.handle}`} 
          style={{ 
            textDecoration: 'none', 
            color: '#e91e63', 
            fontWeight: 500,
            fontSize: (isMobile || isMobileOrSmall) ? '0.75rem' : '0.875rem'
          }}
        >
          {params.value}
        </Link>
      )
    },
    { 
      field: 'handle', 
      headerName: 'Handle', 
      flex: 1, 
      minWidth: (isMobile || isMobileOrSmall) ? 80 : 120,
    },
    { 
      field: 'category_name', 
      headerName: 'Category', 
      flex: 1, 
      minWidth: (isMobile || isMobileOrSmall) ? 80 : 120,
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      flex: 2, 
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%'
        }}>
          <MKTypography 
            sx={{ 
              color: darkMode ? '#ffffff' : '#000000',
              fontSize: '0.875rem'
            }}
          >
            {params.value || 'No description'}
          </MKTypography>
        </Box>
      )
    },
    { 
      field: 'distance_formatted', 
      headerName: 'Distance', 
      flex: 1, 
      minWidth: (isMobile || isMobileOrSmall) ? 80 : 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
          <LocationOn sx={{ 
            fontSize: (isMobile || isMobileOrSmall) ? 14 : 16, 
            mr: 0.5, 
            color: '#e91e63' 
          }} />
          <MKTypography sx={{ 
            color: darkMode ? '#ffffff' : '#000000',
            fontSize: (isMobile || isMobileOrSmall) ? '0.75rem' : '0.875rem'
          }}>
            {params.value || 'N/A'}
          </MKTypography>
        </Box>
      )
    }
  ];

  // Filter columns based on mobile state
  const columns = (isMobile || isMobileOrSmall) 
    ? allColumns.filter(col => !['handle', 'description'].includes(col.field))
    : allColumns;

  useEffect(() => {
    fetchClubs();
  }, [userLocation]);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/api/clubs`;
      
      // If user has location, include coordinates for distance calculation
      if (userLocation) {
        url += `?lat=${userLocation.lat}&lng=${userLocation.lng}`;
      }
      
      const [clubsResponse, categoriesResponse] = await Promise.all([
        fetch(url),
        fetch(`${API_BASE_URL}/api/categories`)
      ]);
      
      const clubsData = await clubsResponse.json();
      const categoriesData = await categoriesResponse.json();
      
      // Filter out any invalid club objects and ensure required fields
      const validClubs = Array.isArray(clubsData)
        ? clubsData.filter(
            (club) => club && club.id && club.name && club.handle && club.created_at
          )
        : [];
      
      setClubs(validClubs);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const requestLocationPermission = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser.');
      setShowLocationAlert(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocationError(null);
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location.';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred while getting location.';
        }
        setLocationError(errorMessage);
        setShowLocationAlert(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const handleViewModeChange = (event, newValue) => {
    setViewMode(newValue);
  };

  // Get shared DataGrid styles
  const dataGridStyles = getDataGridStyles(darkMode);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      overflowX: "hidden", // Prevent horizontal scroll
      px: { xs: 1, sm: 2, md: 3 }
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <MKTypography 
          variant="h4" 
          component="h1" 
          sx={{ 
            color: '#e91e63',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
          }}
        >
          Clubs
        </MKTypography>
        
        {/* Location and View Mode Controls */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 }, 
          alignItems: { xs: 'stretch', sm: 'center' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          {!userLocation && (
            <MKButton
              variant="outlined"
              startIcon={<LocationOn />}
              onClick={requestLocationPermission}
              sx={{ 
                borderColor: '#e91e63', 
                color: '#e91e63',
                width: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Enable Location
            </MKButton>
          )}
          
          {userLocation && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <LocationOn sx={{ color: '#4caf50', fontSize: { xs: 18, sm: 20 } }} />
              <MKTypography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Location enabled
              </MKTypography>
            </Box>
          )}
          
          <Paper sx={{ 
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Tabs 
              value={viewMode} 
              onChange={handleViewModeChange}
              variant={(isMobile || isMobileOrSmall) ? "fullWidth" : "standard"}
              sx={{ 
                '& .MuiTab-root': { 
                  minHeight: { xs: 48, sm: 40 }, 
                  minWidth: { xs: 0, sm: 80 },
                  color: darkMode ? '#fff' : '#666',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 }
                },
                '& .Mui-selected': { 
                  color: '#e91e63' 
                }
              }}
            >
              <Tab 
                value="list" 
                label={(isMobile || isMobileOrSmall) ? "List" : "List View"} 
                icon={<ViewList sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
                iconPosition="start"
              />
              <Tab 
                value="map" 
                label={(isMobile || isMobileOrSmall) ? "Map" : "Map View"} 
                icon={<Map sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
                iconPosition="start"
              />
            </Tabs>
          </Paper>
        </Box>
      </Box>

      {/* View Mode Content */}
      {viewMode === 'list' ? (
        <Box sx={{ 
          height: { xs: 500, sm: 600 }, 
          width: '100%'
        }}>
          <DataGrid
            rows={clubs}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            pageSize={(isMobile || isMobileOrSmall) ? 5 : 10}
            rowsPerPageOptions={(isMobile || isMobileOrSmall) ? [5, 10] : [5, 10, 20]}
            disableSelectionOnClick
            autoHeight
            sx={{
              ...dataGridStyles,
              '& .MuiDataGrid-cell': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: darkMode ? '#ffffff' : '#000000'
              },
              '& .MuiDataGrid-columnHeader': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: darkMode ? '#ffffff' : '#000000',
                backgroundColor: darkMode ? '#2d2d2d' : '#f5f5f5'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: darkMode ? '#ffffff' : '#000000',
                fontWeight: 600
              },
              '& .MuiDataGrid-footerContainer': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: darkMode ? '#ffffff' : '#000000',
                backgroundColor: darkMode ? '#2d2d2d' : '#f5f5f5',
                borderTop: darkMode ? '1px solid #404040' : '1px solid #e0e0e0'
              },
              '& .MuiTablePagination-root': {
                color: darkMode ? '#ffffff' : '#000000'
              },
              '& .MuiTablePagination-select': {
                color: darkMode ? '#ffffff' : '#000000'
              },
              '& .MuiTablePagination-selectIcon': {
                color: darkMode ? '#ffffff' : '#000000'
              },
              '& .MuiTablePagination-actions': {
                color: darkMode ? '#ffffff' : '#000000'
              },
              '& .MuiTablePagination-displayedRows': {
                color: darkMode ? '#ffffff' : '#000000'
              },
              '& .MuiTablePagination-selectLabel': {
                color: darkMode ? '#ffffff' : '#000000'
              },
              '& .MuiIconButton-root': {
                color: darkMode ? '#ffffff' : '#000000',
                '&:hover': {
                  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                }
              }
            }}
            filterModel={{
              items: [],
            }}
            componentsProps={{
              filterPanel: {
                sx: {
                  backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
                  color: darkMode ? '#ffffff' : '#000000',
                },
              },
            }}
          />
        </Box>
      ) : (
        <ClubMapView 
          clubs={clubs}
          userLocation={userLocation}
          onLocationRequest={requestLocationPermission}
        />
      )}

      {/* Location Error Alert */}
      <Snackbar
        open={showLocationAlert}
        autoHideDuration={6000}
        onClose={() => setShowLocationAlert(false)}
        anchorOrigin={{ 
          vertical: 'bottom', 
          horizontal: (isMobile || isMobileOrSmall) ? 'center' : 'right' 
        }}
      >
        <Alert 
          onClose={() => setShowLocationAlert(false)} 
          severity="error" 
          sx={{ 
            width: '100%',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          {locationError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClubsPage;
